import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getDisposalTools,
  getFilterOptions,
  getDisposalPortalSites,
  getSiteTools,
  searchSiteInventory,
} from './api/disposalApi';
import { useUser } from '../../shared/context/UserContext';
import { matchesCanonicalSearch } from '../../shared/searchFilter';
import { MIN_QUERY_LENGTH } from '../../shared/searchFields';
import DisposalToolCard from './components/DisposalToolCard';
import DisposalDetailModal from './components/DisposalDetailModal';
import SiteScopedToolList from './components/SiteScopedToolList';
import './DisposalPortal.css';

/* Canonical snake_case → DisposalPortal camelCase (transformDisposal).
   transformDisposal only produces a subset of canonical fields; unmapped
   canonical names simply won't match (acceptable — still far wider than the
   prior 5-field predicate). NOTE: get_disposal carries no part_numbers/
   asset_tags and this surface is cross-site, so asset-tag / part-number
   search is NOT fixed here — documented punt (Session C). */
const DISPOSAL_PORTAL_FIELD_MAP = {
  supplier_tool_id: 'toolId', alt_tool_id: 'altToolId', oem: 'oem',
  tool_type: 'toolType', part_description: 'partDescription',
  site_location: 'siteLoc', detail_location: 'sectionDetail',
  row_nbr: 'rowNbr', stack_nbr: 'stackNbr', rack_nbr: 'rackNbr',
  shelf_nbr: 'shelfNbr', skid_nbr: 'skidNbr',
};

/* Site label — mirrors S1 AssetRegistryPage.siteLabel for picker parity. */
function siteLabel(s) {
  if (s.site_city) return `${s.site_name} (${s.site_city})`;
  if (s.supplier_site_code) return `${s.site_name} (${s.supplier_site_code})`;
  return s.site_name || 'Unnamed Site';
}

export default function DisposalPortal() {
  const { callerEmail } = useUser();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTool, setSelectedTool] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedMarked, setSelectedMarked] = useState('');
  const [selectedScrapStatus, setSelectedScrapStatus] = useState('');
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    projects: [],
    markedStatuses: [],
    scrapStatuses: []
  });

  // ── Site-picker path (S6 — site-scoped AT/PN search) ───────────────────────
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [siteTools, setSiteTools] = useState([]);                  // get_tools (show-all)
  const [siteToolsLoading, setSiteToolsLoading] = useState(false);
  const [siteToolsError, setSiteToolsError] = useState(null);
  const [siteSearchResults, setSiteSearchResults] = useState(null); // null = not searching
  const searchDebounceRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [toolsData, options] = await Promise.all([
        getDisposalTools({}, callerEmail),
        getFilterOptions(callerEmail)
      ]);
      setTools(toolsData);
      setFilterOptions(options);
    } catch (error) {
      console.error('Error loading disposal tools:', error);
    } finally {
      setLoading(false);
    }
  }, [callerEmail]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* ── Load sites on mount (air-gap enforced server-side) ──────────────────── */
  useEffect(() => {
    if (!callerEmail) return;
    setSitesLoading(true);
    getDisposalPortalSites(callerEmail)
      .then(data => setSites(Array.isArray(data) ? data : (data.sites || [])))
      .catch(() => setSites([]))
      .finally(() => setSitesLoading(false));
  }, [callerEmail]);

  /* ── Load all tools when a site is selected (show-all payload) ───────────── */
  useEffect(() => {
    if (!selectedSiteId || !callerEmail) {
      setSiteTools([]);
      setSiteToolsError(null);
      return;
    }
    setSiteToolsLoading(true);
    setSiteToolsError(null);
    getSiteTools(selectedSiteId, callerEmail)
      .then(data => setSiteTools(Array.isArray(data) ? data : []))
      .catch(err => setSiteToolsError(err.message))
      .finally(() => setSiteToolsLoading(false));
  }, [selectedSiteId, callerEmail]);

  /* ── Debounced canonical search (>= MIN_QUERY_LENGTH) ─────────────────────
     Mirrors S1 AssetRegistryPage: 300ms debounce → search_inventory; shorter
     queries clear results so the full site-tools list shows instead. */
  useEffect(() => {
    const q = (searchQuery || '').trim();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!selectedSiteId || !callerEmail || q.length < MIN_QUERY_LENGTH) {
      setSiteSearchResults(null);
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      searchSiteInventory(selectedSiteId, q, callerEmail)
        .then(data => setSiteSearchResults(Array.isArray(data) ? data : []))
        .catch(() => setSiteSearchResults([]));
    }, 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery, selectedSiteId, callerEmail]);

  const filteredTools = useMemo(() => {
    let filtered = [...tools];

    // Shared canonical filter. Disposal Portal is cross-site (no site_id) and
    // get_disposal carries no part_numbers/asset_tags, so it stays client-side
    // (NOT routed through search_inventory — see Session C closing doc OI).
    // transformDisposal yields camelCase → DISPOSAL_PORTAL_FIELD_MAP; bolNbr/
    // truckNbr kept as surface-specific extras (disposal-workflow tracking).
    if (searchQuery) {
      filtered = filtered.filter(tool =>
        matchesCanonicalSearch(tool, searchQuery, {
          fieldNameMap: DISPOSAL_PORTAL_FIELD_MAP,
          extraFields: ['bolNbr', 'truckNbr'],
        })
      );
    }

    // Only filter if value is not empty string (empty string means "show all")
    if (selectedProject && selectedProject !== "") {
      filtered = filtered.filter(tool => tool.scrapProjectNumber === selectedProject);
    }

    if (selectedMarked && selectedMarked !== "") {
      filtered = filtered.filter(tool => tool.marked === selectedMarked);
    }

    if (selectedScrapStatus && selectedScrapStatus !== "") {
      filtered = filtered.filter(tool => tool.scrapStatus === selectedScrapStatus);
    }

    return filtered;
  }, [tools, searchQuery, selectedProject, selectedMarked, selectedScrapStatus]);

  /* ── Site-picker derived ───────────────────────────────────────────────── */
  const selectedSiteObj = sites.find(s => s.site_id === selectedSiteId);
  const siteNameLabel = selectedSiteObj ? siteLabel(selectedSiteObj) : '';
  const isSiteSearching = (searchQuery || '').trim().length >= MIN_QUERY_LENGTH;
  // D7 — disposal-project membership at the selected site, derived from the
  // already-loaded cross-site disposal list. internalToolId === raw tool_id.
  const siteDisposalToolIds = useMemo(() => {
    if (!selectedSiteId) return new Set();
    return new Set(
      tools.filter(t => t.siteId === selectedSiteId).map(t => t.internalToolId)
    );
  }, [tools, selectedSiteId]);
  const siteScopedDisplay = siteSearchResults !== null ? siteSearchResults : siteTools;

  function handleSiteRowClick(rawTool) {
    const rid = rawTool.tool_id || rawTool.id;
    const disposalTool = tools.find(
      t => t.internalToolId === rid && t.siteId === selectedSiteId
    );
    if (disposalTool) {
      setSelectedTool(disposalTool);
      setShowDetailModal(true);
    }
  }

  function handleToolClick(tool) {
    setSelectedTool(tool);
    setShowDetailModal(true);
  }

  function handleCloseModal() {
    setShowDetailModal(false);
    setSelectedTool(null);
  }

  function handleToolUpdate(updatedTool) {
    // Update tool in list
    setTools(prevTools =>
      prevTools.map(t => t.disposalId === updatedTool.disposalId ? updatedTool : t)
    );
    setSelectedTool(updatedTool);
  }

  function handleClearFilters() {
    setSearchQuery('');
    setSelectedProject('');
    setSelectedMarked('');
    setSelectedScrapStatus('');
  }

  if (loading) {
    return (
      <div className="disposal-portal">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading disposal projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="disposal-portal">
      <header className="disposal-header">
        <h1>DISPOSAL PROJECTS</h1>
      </header>

      <div className="disposal-filters">
        <div className="filter-row">
          <select
            value={selectedSiteId}
            onChange={(e) => { setSelectedSiteId(e.target.value); setSearchQuery(''); }}
            className="filter-select"
            disabled={sitesLoading}
          >
            <option value="">SELECT A SITE…</option>
            {sites.map(s => (
              <option key={s.site_id} value={s.site_id}>{siteLabel(s)}</option>
            ))}
          </select>

          {selectedSiteId && (
            <button
              type="button"
              onClick={() => { setSelectedSiteId(''); setSearchQuery(''); }}
              className="clear-filters-btn"
              title="Clear site — back to disposal projects"
            >
              × Clear Site
            </button>
          )}

          {!selectedSiteId && (
            <>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="filter-select"
              >
                <option value="">PROJECT NUMBER</option>
                {filterOptions.projects.map(proj => (
                  <option key={proj} value={proj}>{proj}</option>
                ))}
              </select>

              <select
                value={selectedMarked}
                onChange={(e) => setSelectedMarked(e.target.value)}
                className="filter-select"
              >
                <option value="">MARKED</option>
                {filterOptions.markedStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>

              <select
                value={selectedScrapStatus}
                onChange={(e) => setSelectedScrapStatus(e.target.value)}
                className="filter-select"
              >
                <option value="">SCRAP STATUS</option>
                {filterOptions.scrapStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>

              <button
                onClick={handleClearFilters}
                className="clear-filters-btn"
                disabled={!searchQuery && !selectedProject && !selectedMarked && !selectedScrapStatus}
              >
                Clear Filters
              </button>
            </>
          )}
        </div>

        <div className="search-row">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by tool ID, site location, truck/BOL number, etc."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {selectedSiteId ? (
        <SiteScopedToolList
          tools={siteScopedDisplay}
          loading={siteToolsLoading}
          error={siteToolsError}
          searchQuery={searchQuery}
          isSearching={isSiteSearching}
          siteName={siteNameLabel}
          disposalToolIds={siteDisposalToolIds}
          onRowClick={handleSiteRowClick}
        />
      ) : (
        <>
          <div className="disposal-stats">
            <div className="stat-item">
              <span className="stat-label">Showing:</span>
              <span className="stat-value">{filteredTools.length} of {tools.length} tools</span>
            </div>
            {selectedProject && (
              <div className="stat-item">
                <span className="stat-label">Project:</span>
                <span className="stat-value">{selectedProject}</span>
              </div>
            )}
          </div>

          {filteredTools.length === 0 ? (
            <div className="no-results">
              <p>No tools found matching your filters.</p>
            </div>
          ) : (
            <div className="disposal-grid">
              {filteredTools.map(tool => (
                <DisposalToolCard
                  key={tool.id}
                  tool={tool}
                  onClick={() => handleToolClick(tool)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {showDetailModal && selectedTool && (
        <DisposalDetailModal
          tool={selectedTool}
          onClose={handleCloseModal}
          onUpdate={handleToolUpdate}
        />
      )}
    </div>
  );
}
